import { useEffect, useState } from "react";
import { Check, FileDown, ArrowRight, ArrowLeft, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { SignaturePad } from "./SignaturePad";

function Progress({ nodes, active }) {
  return (
    <div className="wiz-steps" data-testid="wizard-progress">
      {nodes.map((n, i) => (
        <div key={n.label} className={`wiz-step ${i < active ? "done" : ""} ${i === active ? "active" : ""}`}>
          <span className="wiz-dot">{i < active ? <Check size={13} /> : i + 1}</span>
          <span className="wiz-label">{n.label}</span>
        </div>
      ))}
    </div>
  );
}

export function ContractFlow({ plan, prefill = {}, leadingDone = [], submitLabel = "Payer et activer", busy = false, onComplete, onBack }) {
  const [contract, setContract] = useState(null);
  const [step, setStep] = useState(0); // 0 contract, 1 acceptance, 2 signature
  const [acc, setAcc] = useState({ terms: false, privacy: false, contract: false });
  const [sig, setSig] = useState({ full_name: prefill.full_name || "", company_name: prefill.company_name || "", title: "", signature_data: "" });

  useEffect(() => { api.getContract().then(setContract).catch(() => {}); }, []);

  const base = leadingDone.map((l) => ({ label: l }));
  const nodes = [...base, { label: "Contrat" }, { label: "Acceptation" }, { label: "Signature" }, { label: submitLabel.includes("Payer") ? "Paiement" : "Confirmation" }];
  const active = base.length + step;
  const today = new Date().toLocaleDateString("fr-CA");
  const allAccepted = acc.terms && acc.privacy && acc.contract;
  const canSign = sig.full_name.trim() && sig.signature_data;

  const submit = () => {
    if (!allAccepted) { toast.error("Veuillez accepter les trois documents."); setStep(1); return; }
    if (!canSign) { toast.error("Signature et nom complet requis."); return; }
    onComplete({
      full_name: sig.full_name.trim(), company_name: sig.company_name.trim() || prefill.company_name || "",
      title: sig.title.trim(), signature_data: sig.signature_data,
      accepted_terms: true, accepted_privacy: true, accepted_contract: true,
      contract_version: contract?.version,
    });
  };

  return (
    <div data-testid="contract-flow">
      <Progress nodes={nodes} active={active} />

      {/* STEP 0 — Contract */}
      {step === 0 && (
        <div className="fade-in" data-testid="contract-step">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Contrat de service {contract && <span style={{ color: "var(--muted)", fontWeight: 500, fontSize: 13 }}>· version {contract.version}</span>}</h3>
            <button className="btn btn-outline btn-sm" onClick={() => api.contractPdf()} data-testid="download-contract-pdf"><FileDown size={14} /> Télécharger PDF</button>
          </div>
          <div className="contract-scroll" data-testid="contract-text">
            {!contract && <p style={{ color: "var(--muted)" }}>Chargement du contrat…</p>}
            {contract?.sections.map((s) => (
              <div key={s.title} style={{ marginBottom: 16 }}>
                <h4 style={{ color: "var(--gold)", fontSize: 14, margin: "0 0 6px" }}>{s.title}</h4>
                {s.paragraphs.map((p, i) => <p key={`${s.title}-${i}`} style={{ fontSize: 12.8, lineHeight: 1.6, margin: "0 0 8px", color: "var(--text)" }}>{p}</p>)}
              </div>
            ))}
            {contract?.note && <p style={{ fontSize: 11.5, fontStyle: "italic", color: "var(--muted)" }}>{contract.note}</p>}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16, gap: 10 }}>
            {onBack ? <button className="btn btn-secondary" onClick={onBack} data-testid="contract-back"><ArrowLeft size={15} /> Retour</button> : <span />}
            <button className="btn btn-gold" onClick={() => setStep(1)} data-testid="contract-continue">J'ai lu le contrat <ArrowRight size={15} /></button>
          </div>
        </div>
      )}

      {/* STEP 1 — Acceptance */}
      {step === 1 && (
        <div className="fade-in" data-testid="acceptance-step">
          <h3 style={{ margin: "0 0 4px", fontSize: 16 }}>Acceptation obligatoire</h3>
          <p style={{ color: "var(--muted)", fontSize: 13, margin: "0 0 16px" }}>Cochez les trois cases pour continuer.</p>
          {[
            ["terms", "J'accepte les conditions d'utilisation"],
            ["privacy", "J'accepte la politique de confidentialité (Loi 25)"],
            ["contract", "J'accepte le contrat de service SaaS"],
          ].map(([k, label]) => (
            <label key={k} className="accept-row" data-testid={`accept-${k}`}>
              <input type="checkbox" checked={acc[k]} onChange={(e) => setAcc({ ...acc, [k]: e.target.checked })} data-testid={`accept-${k}-input`} />
              <span>{label}</span>
            </label>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18, gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => setStep(0)}><ArrowLeft size={15} /> Retour</button>
            <button className="btn btn-gold" disabled={!allAccepted} onClick={() => setStep(2)} data-testid="acceptance-continue">Continuer <ArrowRight size={15} /></button>
          </div>
        </div>
      )}

      {/* STEP 2 — Signature */}
      {step === 2 && (
        <div className="fade-in" data-testid="signature-step">
          <h3 style={{ margin: "0 0 12px", fontSize: 16 }}>Signature électronique</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
            <div><label className="field-label">Nom complet *</label><input className="input" style={{ width: "100%" }} value={sig.full_name} onChange={(e) => setSig({ ...sig, full_name: e.target.value })} data-testid="sig-fullname" /></div>
            <div><label className="field-label">Titre / fonction</label><input className="input" style={{ width: "100%" }} value={sig.title} onChange={(e) => setSig({ ...sig, title: e.target.value })} placeholder="Ex. Président" data-testid="sig-title" /></div>
            <div><label className="field-label">Entreprise *</label><input className="input" style={{ width: "100%" }} value={sig.company_name} onChange={(e) => setSig({ ...sig, company_name: e.target.value })} data-testid="sig-company" /></div>
            <div><label className="field-label">Date</label><input className="input" style={{ width: "100%" }} value={today} readOnly data-testid="sig-date" /></div>
          </div>
          <label className="field-label">Signature manuscrite *</label>
          <SignaturePad onChange={(d) => setSig({ ...sig, signature_data: d })} />
          <p style={{ fontSize: 11, color: "var(--muted)", margin: "10px 0 0", display: "flex", alignItems: "center", gap: 6 }}>
            <ShieldCheck size={13} color="var(--gold)" /> Votre adresse IP, l'horodatage et l'appareil sont enregistrés (preuve légale, Loi 25).
          </p>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16, gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => setStep(1)}><ArrowLeft size={15} /> Retour</button>
            <button className="btn btn-gold" disabled={!canSign || busy} onClick={submit} data-testid="sign-submit">
              {busy ? <Loader2 size={15} className="spin" /> : <Check size={15} />} {submitLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
